import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, firstValueFrom } from 'rxjs';
import { DexieDbService } from './dexie-db-service';
import { environment } from '@storees/environments';
import {
  NARRATORS_ROUTE,
  VOICE_CLONE_SLOT_DAD_ID,
  VOICE_CLONE_SLOT_MOM_ID,
  voiceCloneCustomNarratorIdFromDisplayName,
} from '@storees/shared-constants';
import urlJoin from 'url-join';
import type { components } from '@storees/api-types';
import {
  NarratorCountry,
  NarratorEntitlement,
  NarratorGender,
  NarratorLanguage,
  NarratorModel,
  NarratorVoice,
} from '@storees/api-types';
import { stableVoiceCloneAvatarUrlForNarrator } from '../../utils/voice-clone-narrator-avatar';
import { logger } from '../../utils/logger';

type Narrator = components['schemas']['Narrator'];

const { apiBaseUrl } = environment;

export type NarratorWithSortOrder = Narrator & {
  sortOrder: number;
  avatarImageUrl?: string;
};

@Injectable({
  providedIn: 'root',
})
export class DexieNarratorsService {
  private http = inject(HttpClient);
  private db = inject(DexieDbService);

  private narratorSyncQueue: Promise<void> = Promise.resolve();


  private getHeaders(jwtToken: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
    });
  }

  async syncNarrators(jwtToken: string): Promise<void> {
    const queuedSync = this.narratorSyncQueue
      .catch(() => undefined)
      .then(() => this.performNarratorSync(jwtToken));

    this.narratorSyncQueue = queuedSync;
    return queuedSync;
  }

  private async performNarratorSync(jwtToken: string): Promise<void> {
    const headers = this.getHeaders(jwtToken);
    const url = urlJoin(apiBaseUrl, NARRATORS_ROUTE);

    logger.debug('DexieNarratorsService.syncNarrators', url);

    try {
      const serverNarrators = await firstValueFrom(
        this.http.get<Narrator[]>(url, { headers })
      );
      logger.debug(
        'DexieNarratorsService.syncNarrators received narrators',
        serverNarrators?.length ?? 0
      );

      const dedupedNarrators = new Map<string, NarratorWithSortOrder>();
      for (const [index, narrator] of (serverNarrators ?? []).entries()) {
        if (!narrator.id) {
          continue;
        }
        dedupedNarrators.set(narrator.id, {
          ...narrator,
          sortOrder: index,
        });
      }

      await this.db.narrators.clear();
      if (dedupedNarrators.size > 0) {
        await this.db.narrators.bulkPut([...dedupedNarrators.values()]);
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error) {
        const httpError = error as {
          status: number;
          statusText?: string;
          message?: string;
          error?: unknown;
          url?: string;
        };
        logger.error(
          'syncNarrators failed - HTTP Error:',
          JSON.stringify(
            {
              status: httpError.status,
              statusText: httpError.statusText,
              message: httpError.message,
              error: httpError.error,
              url: httpError.url || url,
            },
            null,
            2
          )
        );
      } else if (error instanceof Error) {
        logger.error('syncNarrators failed:', error.message);
      } else {
        logger.error('syncNarrators failed:', JSON.stringify(error));
      }
      throw error;
    }
  }

  getNarrators(): Observable<Narrator[]> {
    return from(this.db.getNarrators());
  }

  getNarrator(id: string): Observable<Narrator | undefined> {
    return from(this.db.getNarrator(id));
  }

  getNarratorsByLanguage(language: string): Observable<Narrator[]> {
    return from(this.db.getNarratorsByLanguage(language));
  }

  getNarratorsByGender(gender: string): Observable<Narrator[]> {
    return from(this.db.getNarratorsByGender(gender));
  }

  static voiceClonePlaceholderNarratorId(voiceType: 'mom' | 'dad'): string {
    return voiceType === 'mom' ? VOICE_CLONE_SLOT_MOM_ID : VOICE_CLONE_SLOT_DAD_ID;
  }

  static buildVoiceClonePlaceholderRecord(
    voiceName: string,
    voiceType: string,
    options?: { narratorId?: string; gender?: NarratorGender; avatarImageUrl?: string }
  ): NarratorWithSortOrder {
    let id: string;
    if (voiceType === 'mom') id = VOICE_CLONE_SLOT_MOM_ID;
    else if (voiceType === 'dad') id = VOICE_CLONE_SLOT_DAD_ID;
    else {
      const explicit = options?.narratorId?.trim();
      id = explicit || voiceCloneCustomNarratorIdFromDisplayName(voiceName);
    }

    let gender: NarratorGender;
    if (voiceType === 'mom') gender = NarratorGender.Female;
    else if (voiceType === 'dad') gender = NarratorGender.Male;
    else gender = options?.gender ?? NarratorGender.Male;

    let avatarImageUrl: string | undefined;
    if (voiceType === 'custom') {
      avatarImageUrl =
        options?.avatarImageUrl?.trim() ||
        stableVoiceCloneAvatarUrlForNarrator(id, gender);
    }

    return {
      id,
      model: NarratorModel.ChatterboxTurbo,
      voice: NarratorVoice.voice_clone,
      language: NarratorLanguage.AmericanEnglish,
      name: voiceName,
      gender,
      country: NarratorCountry.US,
      entitlement: NarratorEntitlement.Bronze,
      durationMs: 0,
      isUploadComplete: true,
      sortOrder: 999,
      ...(avatarImageUrl ? { avatarImageUrl } : {}),
    };
  }

  async addVoiceCloneNarratorPlaceholder(
    voiceName: string,
    voiceType: string,
    options?: { narratorId?: string; gender?: NarratorGender; avatarImageUrl?: string }
  ): Promise<void> {
    const narrator = DexieNarratorsService.buildVoiceClonePlaceholderRecord(
      voiceName,
      voiceType,
      options
    );
    await this.db.narrators.put(narrator as Narrator & { sortOrder: number });
  }

  async removeVoiceCloneNarratorById(narratorId: string): Promise<void> {
    await this.db.deleteNarrator(narratorId);
  }

  async getVoiceCloneNarrators(): Promise<NarratorWithSortOrder[]> {
    const narrators = await this.db.getNarrators();
    return narrators.filter(
      (narrator): narrator is NarratorWithSortOrder =>
        narrator.voice === NarratorVoice.voice_clone &&
        !!narrator.userId &&
        !narrator.deletedAt
    ) as NarratorWithSortOrder[];
  }

  async getVoiceCloneNarratorById(
    id: string
  ): Promise<NarratorWithSortOrder | undefined> {
    const narrator = await this.db.getNarrator(id);
    if (
      narrator?.voice !== NarratorVoice.voice_clone ||
      !narrator.userId ||
      narrator.deletedAt
    ) {
      return undefined;
    }

    return narrator as NarratorWithSortOrder;
  }
}
